import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { T, sans } from '../pages/Vendors/VendorDetails';

/**
 * The platform-wide AI guardrail's reactive side. backend/middleware/ai_guard.py refuses every
 * scan trigger, vendor-creating action and questionnaire send with a 412 until the tenant has a
 * verified-working AI provider (a saved key alone isn't enough -- it must have actually passed a
 * real Test Connection call). services/api.ts's response interceptor catches that 412 globally,
 * for any request, and dispatches a 'fluxwatch:ai-blocked' window event -- this component is the
 * only listener, mounted once in MainLayout so the popup appears no matter which button, page or
 * future code path triggered the block. This is deliberately NOT the security boundary: it only
 * reacts to what the server already refused, so there is nothing here for a tampered request to
 * bypass -- hiding or skipping this component would just mean the user sees no explanation, not
 * that the action goes through.
 */

interface BlockedDetail {
  aiMode: 'byok' | 'platform';
  message: string;
}

export default function AIGuardModal() {
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const navigate = useNavigate();
  const [detail, setDetail] = useState<BlockedDetail | null>(null);

  useEffect(() => {
    const onBlocked = (e: Event) => setDetail((e as CustomEvent<BlockedDetail>).detail);
    window.addEventListener('fluxwatch:ai-blocked', onBlocked);
    return () => window.removeEventListener('fluxwatch:ai-blocked', onBlocked);
  }, []);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDetail(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail]);

  if (!detail) return null;

  const isByok = detail.aiMode === 'byok';
  const c = {
    overlay: 'rgba(0, 0, 0, 0.68)',
    surface: isDark ? '#141414' : '#ffffff',
    border: T.red,
    ink: isDark ? '#f1f5f9' : T.ink,
    body: isDark ? '#cbd5e1' : T.ink2,
    muted: isDark ? '#94a3b8' : T.slate,
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="ai-guard-title"
      onClick={() => setDetail(null)}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: c.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        fontFamily: sans,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(480px, 100%)', background: c.surface, borderRadius: 14,
          border: `2px solid ${c.border}`, boxShadow: '0 24px 64px rgba(220,38,38,0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ background: T.red, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 999, background: 'rgba(255,255,255,0.18)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            fontWeight: 800, color: '#fff', flexShrink: 0,
          }}>!</span>
          <div id="ai-guard-title" style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>
            AI provider required
          </div>
        </div>

        <div style={{ padding: '22px 24px' }}>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: c.ink, fontWeight: 600 }}>
            {isByok
              ? "This action needs a working AI provider, and your workspace doesn't have one yet."
              : "This action needs a working AI provider, and FluxWatch hasn't finished setting yours up yet."}
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 13.5, lineHeight: 1.6, color: c.body }}>
            {isByok
              ? 'Scans, adding vendors and sending questionnaires are switched off for your workspace until an AI provider key is added and successfully tested in Settings > AI Providers. A key that has never been tested, or failed its test, does not count.'
              : "Scans, adding vendors and sending questionnaires are switched off for your workspace until FluxWatch finishes configuring and testing your AI provider. This is not something you can fix here — please contact FluxWatch support."}
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: c.muted }}>
            {detail.message}
          </p>

          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button
              onClick={() => setDetail(null)}
              style={{
                height: 36, padding: '0 16px', borderRadius: 8, border: `1px solid ${isDark ? '#2a2a2a' : T.bdr}`,
                background: 'transparent', color: c.body, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: sans,
              }}
            >
              Dismiss
            </button>
            {isByok ? (
              <button
                onClick={() => { setDetail(null); navigate('/settings?section=ai-providers'); }}
                style={{
                  height: 36, padding: '0 16px', borderRadius: 8, border: 'none',
                  background: T.red, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: sans,
                }}
              >
                Go to AI Providers
              </button>
            ) : (
              <a
                href="mailto:support@fluxvell.com?subject=AI%20provider%20not%20configured"
                onClick={() => setDetail(null)}
                style={{
                  height: 36, padding: '0 16px', borderRadius: 8, border: 'none',
                  background: T.red, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: sans,
                  display: 'inline-flex', alignItems: 'center', textDecoration: 'none',
                }}
              >
                Contact support
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
